import inputVariables from "@krystal/css/components/input.css?inline";
import { nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { html, unsafeStatic } from "lit/static-html.js";
import "@/components/visually-hidden";
import "@/components/tooltip";
import "@/components/icon";
import "@/components/admin-icon";
import EkoElement from "@/utilities/element";
import { getSafeTag, type Tag } from "@/utilities/html";
import style from "./label.css?inline";

@customElement("eko-label")
export class Label extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(inputVariables), unsafeCSS(style)];

  @property({ type: Boolean }) admin: boolean = false;
  @property({ reflect: false }) as?: Tag; // default is `label`
  @property() error: string = "";
  @property() for: string = "";
  @property() help: string = "";
  @property({ type: Boolean }) hidden: boolean = false;
  @property({ type: Boolean }) required: boolean = false;

  private handleClick() {
    if (this.for) {
      const target = document.getElementById(this.for);

      if (target && typeof target.focus === "function") {
        target.focus();
      }
    }
  }

  render() {
    return this.hidden ? this.renderHidden() : this.renderLabel();
  }

  renderHidden() {
    return html`<visually-hidden> ${this.renderLabel()} </visually-hidden>`;
  }

  renderLabel() {
    return html`<${unsafeStatic(this.tag)} for=${this.for} class="eko-label" @click=${this.handleClick} part="root">
      <slot></slot>
      ${this.required ? html`<span class="eko-label__required" part="required">*</span>` : nothing}
      ${
        this.help || this.hasError
          ? html`
              <eko-tooltip
                position="right"
                arrow
                aria-label=${this.help || this.error}
                class="eko-label__help"
                part="help">
                <eko-icon
                  name=${this.hasError ? "alert-circle" : "help-circle"}
                  theme="current"
                  size="md"
                  part="help-icon"></eko-icon>
                <span slot="content">${this.help || this.error}</span>
              </eko-tooltip>
            `
          : nothing
      }
      ${
        this.admin
          ? html`<span class="eko-label__admin"><eko-admin-icon tooltip></eko-admin-icon></span>`
          : nothing
      }
    </${unsafeStatic(this.tag)}>`;
  }

  private get hasError(): boolean {
    return this.error !== "";
  }

  private get tag() {
    return getSafeTag(this.as, "label");
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-label": Label;
  }
}

export default Label;
